/**
 * Module Qualité (20_QUAL) — enums, constantes et matrices de transition.
 * Miroir des enums `src/domain/quality.py` (VARCHAR+CHECK en base, donc unions
 * de littéraux côté TS). Ré-exporté par `useQuality.types.ts`.
 */

// =============================================================================
// Enums (valeurs figées, iso backend)
// =============================================================================
export type NCSeverity = 'minor' | 'major' | 'critical'
export type NCStatus = 'open' | 'in_analysis' | 'in_progress' | 'verification' | 'closed'
export type NCSourceChannel =
    | 'manual'
    | 'session'
    | 'client_portal'
    | 'satisfaction'
    | 'formascore'
    | 'qualiopi'
export type QualityReferentialType =
    | 'NC_ORIGIN'
    | 'NC_VECTOR'
    | 'NC_CAUSE'
    | 'REQUESTER_CATEGORY'
    | 'REQUESTER_STRUCTURE'

export type ActionType = 'corrective' | 'preventive' | 'improvement'
export type ActionStatus = 'todo' | 'in_progress' | 'done' | 'cancelled'
export type ActionSource =
    | 'non_conformity'
    | 'formascore'
    | 'qualiopi'
    | 'veille'
    | 'audit'
    | 'manual'

export const NC_SEVERITIES: NCSeverity[] = ['minor', 'major', 'critical']
export const NC_STATUSES: NCStatus[] = [
    'open',
    'in_analysis',
    'in_progress',
    'verification',
    'closed',
]
export const ACTION_TYPES: ActionType[] = ['corrective', 'preventive', 'improvement']
export const ACTION_STATUSES: ActionStatus[] = ['todo', 'in_progress', 'done', 'cancelled']

// SLA (jours) par gravité — iso `SLA_DAYS` backend (indicatif UI).
export const NC_SLA_DAYS: Record<NCSeverity, number> = { minor: 30, major: 15, critical: 2 }

// Indicateur Qualiopi 26 (accessibilité) → plancher de gravité `major`.
export const QUALIOPI_INDICATOR_DISABILITY = '26'

// Matrices de transition (iso `NC_ALLOWED_TRANSITIONS` / `ACTION_ALLOWED_TRANSITIONS`).
// Servent à n'afficher que les transitions autorisées (boutons + drag Kanban).
export const NC_ALLOWED_TRANSITIONS: Record<NCStatus, NCStatus[]> = {
    // Libre circulation avant/arrière entre états actifs (correction d'erreur PO) +
    // clôture depuis tout état actif + réouverture vers tout état actif. Iso backend.
    open: ['in_analysis', 'in_progress', 'verification', 'closed'],
    in_analysis: ['open', 'in_progress', 'verification', 'closed'],
    in_progress: ['open', 'in_analysis', 'verification', 'closed'],
    verification: ['open', 'in_analysis', 'in_progress', 'closed'],
    closed: ['open', 'in_analysis', 'in_progress', 'verification'],
}
export const ACTION_ALLOWED_TRANSITIONS: Record<ActionStatus, ActionStatus[]> = {
    todo: ['in_progress', 'done', 'cancelled'],
    in_progress: ['todo', 'done', 'cancelled'],
    done: ['in_progress'],
    cancelled: ['todo', 'in_progress'],
}

export function ncTransitionAllowed(current: NCStatus, target: NCStatus): boolean {
    if (current === target) return false
    return (NC_ALLOWED_TRANSITIONS[current] ?? []).includes(target)
}
export function actionTransitionAllowed(current: ActionStatus, target: ActionStatus): boolean {
    if (current === target) return false
    return (ACTION_ALLOWED_TRANSITIONS[current] ?? []).includes(target)
}

// Fermeture NC ⇒ permission `quality:close` (source OU cible = closed).
export function ncTransitionRequiresClose(current: NCStatus, target: NCStatus): boolean {
    return current === 'closed' || target === 'closed'
}

// Vocabulaire OUVERT des objets sources (points d'entrée transverses §5.5).
// Miroir de `backend/src/domain/quality.SOURCE_OBJECT_TYPES`.
//
// ⚠️ Un type désigne UN écran, pas une famille d'objets. `product` couvrait à la
// fois les formations du catalogue et les articles — deux tables, deux fiches :
// une NC signalée depuis un ARTICLE renvoyait vers une fiche formation
// inexistante. D'où `formation` / `article`, comme dans le module Tâches.
//
// L'ancien `product` est volontairement ABSENT d'ici : il reste accepté en
// lecture par le serveur (anciennes NC), mais son retrait du type TS fait
// échouer la compilation si un écran tentait de le ré-émettre.
export const SOURCE_OBJECT_TYPES = [
    'session',
    'learner',
    'client',
    'instructor',
    'quote',
    'invoice',
    'expense',
    'location',
    'hotel',
    'restaurant',
    'formation',
    'article',
    'satisfaction_response',
    'client_complaint',
    // 30_LOG / RSC — le matériel de formation. Une VGP dépassée constatée
    // sur un chariot est une non-conformité comme une autre.
    //
    // ⚠️ Aucun slot dans `SOURCE_TO_CONTEXT` : l'onglet Contexte liste les
    // PARTIES PRENANTES (client, apprenant, intervenant, lieu…), pas les
    // objets. Lui inventer un slot ferait pré-remplir « Lieu » avec un
    // identifiant de matériel. Sa traçabilité passe par `source_object_*`,
    // comme pour un article.
    'resource',
    // 40_TENDER — l'appel d'offres. Un pli déposé hors délai, une pièce
    // obligatoire manquante ou une réponse qui promet ce qu'on ne sait pas
    // tenir sont des non-conformités comme les autres — et sur un marché
    // public, ce sont celles qui coûtent le plus cher.
    //
    // ⚠️ Aucun slot dans `SOURCE_TO_CONTEXT` : l'onglet Contexte liste les
    // PARTIES PRENANTES (client, apprenant, intervenant, lieu…), pas les
    // objets. L'acheteur d'un AO vit INLINE sur la fiche et n'est pas un
    // client tant que le marché n'est pas gagné : lui inventer un slot ferait
    // pré-remplir « Client » avec quelqu'un qui n'existe pas au CRM. Sa
    // traçabilité passe par `source_object_*`, comme pour un matériel.
    'tender',
] as const
export type SourceObjectType = (typeof SOURCE_OBJECT_TYPES)[number]
